import { useEffect, useState } from "react"
import * as Sentry from "@sentry/react"
import { useStore } from "@/store"
import {
  FetchStatus,
  useGetWorkspacesApi,
  workspacesResponseToWorkspaces,
} from "@/hooks/api"
import { LoadingPage, ErrorPage } from "@/components/System"
import { DEFAULT_LOADING_MESSAGE } from "@/constants"
import { Organization } from "@/types"
import { useTranslation } from "react-i18next"

const DataLoader: React.FC<{
  organization: Organization
  children: React.ReactNode
}> = ({ organization, children }) => {
  const { t } = useTranslation()
  const [setWorkspaces] = useStore((state) => [state.setWorkspaces])

  const {
    status: workspacesStatus,
    error: workspacesError,
    data: workspacesData,
    fetchFn: getWorkspacesApi,
  } = useGetWorkspacesApi(organization.id)
  const [isLoading, setIsLoading] = useState(true)
  const [workspaceLoaded, setWorkspaceLoaded] = useState(false)

  useEffect(() => {
    if (workspacesStatus !== FetchStatus.Idle) return

    void getWorkspacesApi().catch((error: unknown) => {
      // useFetch exposes the error to ErrorPage; keep it observable as handled.
      Sentry.captureException(error, { tags: { source: "data-loader" } })
    })
  }, [workspacesStatus, getWorkspacesApi])

  // Handle workspaces data
  useEffect(() => {
    if (!workspacesData || !workspacesData.data) return

    const newWorkspaces = workspacesResponseToWorkspaces(workspacesData)
    setWorkspaces(newWorkspaces)
    setWorkspaceLoaded(true)
  }, [workspacesData, setWorkspaces])

  useEffect(() => {
    if (workspaceLoaded) {
      setIsLoading(false)
      console.debug("Data loaded.")
      return
    }
    setIsLoading(true)
  }, [workspaceLoaded])

  return (
    <>
      {workspacesError ? (
        <ErrorPage message="Failed to load the user data, please refresh the page or go back home." />
      ) : isLoading ? (
        <LoadingPage message={t(DEFAULT_LOADING_MESSAGE)} />
      ) : (
        children
      )}
    </>
  )
}

export default DataLoader
